import { useEffect } from 'react';

import { Flex, FloraButton, Toaster } from '@grupoboticario/flora-react';
import { RichText } from 'prismic-reactjs';
import { Link } from 'react-router-dom';

import { ResponsiveFloraTypography } from 'components';
import { parseToStarlink } from 'helpers';
import { useIncentiveCampaign, useUrlTrackingParams, useTracking, useResellerDetail } from 'hooks';
import { newRelicMonitoringService } from 'services';

import Styles from './OptIn.styles';
import { useLoyaltyIncentiveCampaign } from '../../hooks';

import type { AxiosResponse } from 'axios';

export const OptIn = () => {
  const { optin } = useIncentiveCampaign;
  const {
    data: incentiveCampaign,
    campaignId,
    isError: loyaltyError,
    isOptinClosed,
  } = useLoyaltyIncentiveCampaign();
  const regulationsUrl = useUrlTrackingParams(incentiveCampaign?.regulations?.url ?? '');
  const useTermsUrl = useUrlTrackingParams(incentiveCampaign?.useTerms?.url ?? '');
  const policiesUrl = useUrlTrackingParams(incentiveCampaign?.privacyPolicies?.url ?? '');
  const { data: resellerDetail, isError: isResellerError } = useResellerDetail();
  const isLoyaltyMember = resellerDetail?.reseller.isLoyaltyMember ?? false;
  const isError = loyaltyError || isResellerError;
  const { data: optInData, refetch } = optin.useQuery();

  const {
    mutate: optInToCampaign,
    isLoading,
    isSuccess: isOptinSuccess,
    isError: isOptinError,
    error: optinError,
  } = optin.useMutation(campaignId);
  const { sendDataToGTM } = useTracking();

  const handleOptInClick = () => {
    optInToCampaign(campaignId);
  };

  useEffect(() => {
    if (isOptinSuccess) {
      window.location.reload();
      refetch();
    }

    if (isOptinError) {
      Toaster.notify({
        kind: 'error',
        description:
          'Não foi possível registrar sua participação na campanha. Por favor, tente novamente.',
        origin: 'right-bottom',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refetch, optinError, isOptinSuccess]);

  useEffect(() => {
    if (optInData && (isOptinError || isOptinSuccess)) {
      const error = optinError as AxiosResponse;

      newRelicMonitoringService.addPageAction('callback', {
        event_name: 'callback_optin_campanha_de_incentivos',
        page_title: incentiveCampaign?.pageTitle,
        cd_interaction_detail: isOptinSuccess ? 'sucesso' : `erro: ${error?.status}`,
      });

      sendDataToGTM({
        event: 'analytics-event',
        event_parameters: {
          event_name: 'callback_optin_campanha_de_incentivos',
          cd_detail: isOptinSuccess ? 'sucesso' : `erro: ${error?.status}`,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOptinSuccess, isOptinError]);

  if (isError) return <> </>;

  return (
    <Flex direction='column' gap='$6'>
      <ResponsiveFloraTypography color='$nonInteractiveAuxiliar'>
        {incentiveCampaign && (
          <RichText
            render={isLoyaltyMember ? incentiveCampaign.mcmRules : incentiveCampaign.rules}
          />
        )}
      </ResponsiveFloraTypography>

      <Styles.OptIn>
        <Flex direction='column' gap='$6'>
          {!isOptinClosed && (
            <ResponsiveFloraTypography color='$nonInteractiveAuxiliar'>
              Ao participar da campanha, declaro que li e concordo com o{' '}
              <Link
                target='_blank'
                autotagging-flora='on'
                data-flora='link'
                data-flora-text='regulamento-da-campanha'
                to={parseToStarlink(regulationsUrl)}
              >
                <ResponsiveFloraTypography
                  color='$actionableDefault'
                  textDecoration='underline'
                  as='span'
                >
                  regulamento da campanha
                </ResponsiveFloraTypography>
              </Link>{' '}
              ,{' '}
              <Link
                to={parseToStarlink(useTermsUrl)}
                target='_blank'
                autotagging-flora='on'
                data-flora='link'
                data-flora-text='termos-de-uso'
              >
                <ResponsiveFloraTypography
                  color='$actionableDefault'
                  textDecoration='underline'
                  as='span'
                >
                  termos de uso
                </ResponsiveFloraTypography>
              </Link>{' '}
              e{' '}
              <Link
                to={parseToStarlink(policiesUrl)}
                target='_blank'
                autotagging-flora='on'
                data-flora='link'
                data-flora-text='politica-de-privacidade'
              >
                <ResponsiveFloraTypography
                  color='$actionableDefault'
                  textDecoration='underline'
                  as='span'
                >
                  política de privacidade
                </ResponsiveFloraTypography>
              </Link>{' '}
              do parceiro para o resgate dos prêmios.
            </ResponsiveFloraTypography>
          )}

          <Styles.OptInButtonWrapper>
            <FloraButton
              disabled={isLoading || isOptinClosed}
              onClick={handleOptInClick}
              isLoading={isLoading}
              autotagging-flora='on'
              data-flora='button'
              data-flora-text='participar-da-campanha'
              styleSemantic='conversion'
            >
              Participar da campanha
            </FloraButton>
          </Styles.OptInButtonWrapper>
        </Flex>
      </Styles.OptIn>
    </Flex>
  );
};
